import React from 'react';
import classNames from 'classnames';

import Field, { hasDataInFields } from '../../Field';
import SectionContainer from '../SectionContainer';
import landingPagePhotos from '../landingPagePhotos';

import css from './SectionImageBanner.module.css';

/**
 * Full-bleed photo band with a scrim, centered title and an optional CTA button.
 * Two variants: 'tall' (page hero, title anchored toward the bottom) and the
 * default band (centered title + CTA), both from the About page design.
 *
 * @component
 * @param {Object} props
 * @param {string?} props.className
 * @param {string?} props.rootClassName
 * @param {string} props.sectionId
 * @param {'imageBanner'} props.sectionType
 * @param {keyof landingPagePhotos} props.photo which registered photo to render
 * @param {string?} props.alt accessible description of the photo
 * @param {'tall'?} props.variant 'tall' anchors the title low, like a page hero
 * @param {Object?} props.title title (Field data)
 * @param {Object?} props.callToAction { content, href } button
 * @param {Object?} props.appearance
 * @param {Object} props.options
 * @returns {JSX.Element}
 */
const SectionImageBanner = props => {
  const {
    sectionId,
    className,
    rootClassName,
    photo,
    alt = '',
    variant,
    title,
    callToAction,
    appearance,
    options,
  } = props;

  const fieldOptions = { fieldComponents: options?.fieldComponents };
  const hasTitle = hasDataInFields([title], fieldOptions);
  const photoSrc = photo && landingPagePhotos[photo];
  const isTall = variant === 'tall';

  return (
    <SectionContainer
      id={sectionId}
      className={classNames(rootClassName || css.root, className, { [css.tall]: isTall })}
      appearance={appearance}
      options={fieldOptions}
    >
      <div
        className={classNames(css.band, { [css.bandTall]: isTall })}
        style={photoSrc ? { backgroundImage: `url(${photoSrc})` } : null}
        role={alt ? 'img' : null}
        aria-label={alt || null}
      >
        <div className={css.scrim} />
        <div className={classNames(css.content, { [css.contentTall]: isTall })}>
          {hasTitle ? <Field data={title} className={css.title} options={fieldOptions} /> : null}
          {callToAction?.content && callToAction?.href ? (
            <a className={css.cta} href={callToAction.href}>
              {callToAction.content}
            </a>
          ) : null}
        </div>
      </div>
    </SectionContainer>
  );
};

export default SectionImageBanner;
